import { useEffect, useRef, type MutableRefObject } from "react";
import { ExportButton } from "@/components/ExportButton";
import type { Exporter, Identifier, RaRecord } from "react-admin";
import {
  Datagrid,
  List,
  TextField,
  FunctionField,
  SimpleList,
  useListContext,
  usePermissions,
  Pagination,
  EditButton,
  TopToolbar,
  CreateButton,
} from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import { formatListDate } from "@/utils/date-format";
import DisplayDateField from "@/components/DisplayDateField";
import { Box, IconButton, useMediaQuery } from "@mui/material";
import { exportToExcel } from "@/utils/export-to-excel";
import { isLagatTallyConfigReady } from "@/types/tally-config";
import type { ExcelColumn } from "@/types/excel";
import { formatINR } from "@/utils/app-formatters";
import { hasPermission } from "@/utils/permission-utils";
import TallyExportButton from "@/components/TallyExportButton";
import StackedDatagridCell from "@/components/StackedDatagridCell";
import LagatReceiptFilterBar from "./LagatReceiptFilters";
import LagatReceiptPaymentSummary from "./LagatReceiptPaymentSummary";
import {
  type LagatReceiptRow,
  DEFAULT_PAYMENT_MODE_FILTER,
  getFilterFromURL,
  lagatExportFilenameSuffix,
  openReceiptPrint,
  purposeLabel,
} from "./lagat-receipt-utils";

const ReceiptDownloadAction = ({
  recordId,
  stopPropagation = false,
}: {
  recordId: string | number;
  stopPropagation?: boolean;
}) => (
  <IconButton
    size="small"
    color="primary"
    aria-label="Download receipt"
    onClick={(event) => {
      if (stopPropagation) {
        event.stopPropagation();
        event.preventDefault();
      }
      openReceiptPrint(recordId);
    }}
  >
    <DownloadIcon fontSize="small" />
  </IconButton>
);

const simpleListSx = {
  "& .MuiListItemButton-root": { alignItems: "center" },
  "& .MuiListItemIcon-root": { minWidth: "auto", justifyContent: "center", alignItems: "center" },
} as const;

const ReceiptListBody = ({
  isSmall,
  canEditLagat,
}: {
  isSmall: boolean;
  canEditLagat: boolean;
}) => {
  if (isSmall) {
    return (
      <SimpleList
        primaryText={(record) => String(record.receiptNo ?? "—")}
        secondaryText={(record) => {
          const row = record as LagatReceiptRow;
          const dateLabel = row.receiptDate ? formatListDate(row.receiptDate as string) : "—";
          return (
            <>
              {row.name ?? "—"}
              {row.itsNo ? ` · ITS ${row.itsNo}` : ""}
              <br />
              {purposeLabel(row)} · {dateLabel} · {formatINR(row.amount)} ·{" "}
              {String(row.paymentMode ?? "—")}
            </>
          );
        }}
        tertiaryText={(record) => {
          const remarks = String((record as LagatReceiptRow).remarks ?? "").trim();
          return remarks || "—";
        }}
        linkType="edit"
        rightIcon={(record) => <ReceiptDownloadAction recordId={record.id} stopPropagation />}
        sx={simpleListSx}
        rowSx={() => ({ borderBottom: 1, borderBottomColor: "divider" })}
      />
    );
  }

  return (
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      {canEditLagat ? <EditButton /> : null}
      <TextField source="receiptNo" />
      <FunctionField
        label="Name"
        sortBy="name"
        render={(record: LagatReceiptRow) => (
          <StackedDatagridCell
            primary={record.name ?? "—"}
            secondary={record.itsNo ? `ITS ${record.itsNo}` : "—"}
          />
        )}
      />
      <FunctionField
        label="Payment"
        sortBy="amount"
        render={(record: LagatReceiptRow) => (
          <StackedDatagridCell
            align="right"
            primaryBold
            primary={formatINR(record.amount)}
            secondary={String(record.paymentMode ?? "—")}
          />
        )}
      />
      <FunctionField
        label="Purpose"
        sortBy="purpose"
        render={(record: LagatReceiptRow) => purposeLabel(record)}
      />
      <DisplayDateField source="receiptDate" label="Receipt date" emptyText="—" />
      <TextField source="remarks" emptyText="—" />
      <FunctionField
        label="Download"
        source="formNo"
        sortable={false}
        render={(record: LagatReceiptRow) => (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <ReceiptDownloadAction recordId={record.id} stopPropagation />
          </Box>
        )}
      />
    </Datagrid>
  );
};

const listToolbarSx = {
  justifyContent: "flex-start",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  rowGap: 1,
  width: "100%",
  px: { xs: 1, sm: 1.5 },
  py: 0.75,
  minHeight: 48,
  "& .MuiButton-root": {
    minHeight: 32,
    py: 0.5,
    px: 1.25,
    fontSize: "0.8125rem",
  },
  "& .MuiIconButton-root": { width: 32, height: 32 },
} as const;

const listToolbarActionsSx = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  rowGap: 1,
  ml: "auto",
} as const;

const ExportFilterSync = ({ paymentModeRef }: { paymentModeRef: MutableRefObject<string> }) => {
  const { filterValues } = useListContext();
  useEffect(() => {
    if (filterValues?.paymentMode) {
      paymentModeRef.current = String(filterValues.paymentMode);
    }
  }, [filterValues?.paymentMode, paymentModeRef]);
  return null;
};

const LagatListActions = () => {
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, "lagatReceipts.view");
  const canCreate =
    hasPermission(permissions, "lagatReceipts.create") ||
    hasPermission(permissions, "bookingReceipts.create");

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
      <TopToolbar sx={listToolbarSx}>
        <Box sx={listToolbarActionsSx}>
          {canCreate ? <CreateButton size="small" /> : null}
          {canView ? <ExportButton size="small" /> : null}
          {canView ? (
            <TallyExportButton
              module="lagat"
              filenamePrefix="lagat-tally"
              isConfigReady={isLagatTallyConfigReady}
            />
          ) : null}
        </Box>
      </TopToolbar>
      <LagatReceiptFilterBar />
    </Box>
  );
};

const receiptExportColumns: ExcelColumn<RaRecord<Identifier>>[] = [
  { header: "Receipt No", field: "receiptNo", width: 15 },
  { header: "Name", field: "name", width: 30 },
  { header: "ITS No.", field: "itsNo", width: 12 },
  {
    header: "Amount",
    field: "amount",
    width: 12,
    formatter: (_rec, v) => formatINR(v),
  },
  { header: "Purpose", field: "purpose", width: 25 },
  { header: "Payment Mode", field: "paymentMode", width: 15 },
  { header: "Payment Ref", field: "paymentRef", width: 20 },
  {
    header: "Receipt Date",
    field: "receiptDate",
    width: 15,
    formatter: (_rec, v) => (v ? formatListDate(String(v)) : ""),
  },
  { header: "Remarks", field: "remarks", width: 28 },
];

const LagatReceiptList = () => {
  const { permissions } = usePermissions();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const exportPaymentModeRef = useRef(DEFAULT_PAYMENT_MODE_FILTER);
  const canView = hasPermission(permissions, "lagatReceipts.view");
  const canEditLagat = hasPermission(permissions, "lagatReceipts.edit");

  const exporter = (records: RaRecord[]) => {
    const tabName = lagatExportFilenameSuffix(exportPaymentModeRef.current);
    return exportToExcel(receiptExportColumns, records, {
      filenamePrefix: `lagatreceipts-${tabName}`,
      sheetName: "Lagat Receipts",
    });
  };

  return (
    <List
      sort={{ field: "receiptNo", order: "DESC" }}
      filterDefaultValues={getFilterFromURL()}
      actions={<LagatListActions />}
      exporter={canView ? (exporter as unknown as Exporter<RaRecord<Identifier>>) : false}
      pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
      sx={{
        "& .RaList-actions": {
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
        },
        "& .RaList-actions > .MuiToolbar-root": listToolbarSx,
      }}
    >
      <ExportFilterSync paymentModeRef={exportPaymentModeRef} />
      <LagatReceiptPaymentSummary />
      <ReceiptListBody isSmall={isSmall} canEditLagat={canEditLagat} />
    </List>
  );
};

export default LagatReceiptList;
